import { useEffect, useState } from "react";
import { Boxes, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { mxn, fdatetime } from "@/lib/format";
import { toast } from "sonner";

const BADGE = {
  Pendiente: "bg-amber-100 text-amber-800",
  Aprobada: "bg-emerald-100 text-emerald-700",
  Rechazada: "bg-red-100 text-red-700",
};

// ago-2026 — Solicitudes de producto de crédito extra ($500 MXN/mes + IVA). El cobro es por
// TRANSFERENCIA: aprobar aquí aplica la recarga manual del cupo (`entidades.productos_extra`).
// Stripe no participa en ningún punto de este flujo.
export function PanelProductoExtra() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [rechazo, setRechazo] = useState(null);
  const [motivo, setMotivo] = useState("");

  const load = () =>
    api.get("/admin/solicitudes-producto-extra")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));

  useEffect(() => { load(); }, []);

  const aprobar = async (s) => {
    setBusy(s.id);
    try {
      const { data } = await api.post(`/admin/solicitudes-producto-extra/${s.id}/aprobar`, {});
      toast.success(`Cupo ampliado. Extra total de la entidad: ${data?.productos_extra_nuevo}.`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  const confirmarRechazo = async () => {
    if (!motivo.trim()) { toast.error("Indica el motivo del rechazo"); return; }
    setBusy(rechazo.id);
    try {
      await api.post(`/admin/solicitudes-producto-extra/${rechazo.id}/rechazar`, { motivo });
      toast.success("Solicitud rechazada.");
      setRechazo(null); setMotivo("");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  const pendientes = items.filter((i) => i.estatus === "Pendiente").length;

  return (
    <section data-testid="panel-producto-extra"
      className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
          <Boxes className="h-5 w-5 text-primary" />
        </span>
        <div>
          <p className="font-heading text-sm font-semibold text-slate-900">
            Solicitudes de producto de crédito extra
            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800"
              data-testid="producto-extra-pendientes-count">{pendientes}</span>
          </p>
          <p className="text-xs text-slate-500">
            $500 MXN/mes + IVA por producto. Cobro por transferencia bancaria: al aprobar se amplía
            el cupo de la entidad (nunca pasa por Stripe).
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {items.length === 0 && (
          <p className="text-sm text-slate-400" data-testid="producto-extra-vacio">
            Sin solicitudes registradas.
          </p>
        )}
        {items.map((s) => (
          <div key={s.id} data-testid={`producto-extra-row-${s.id}`}
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-100 bg-slate-50/60 p-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-800">{s.entidad_nombre}</p>
              <p className="text-xs text-slate-500">
                {fdatetime(s.creado_at)} · {s.cantidad} producto(s) · {mxn(s.monto_mensual || 0)}/mes + IVA
                · plan {s.plan_id || "—"} · {s.solicitado_por_email}
              </p>
              {s.nota && <p className="mt-0.5 text-xs italic text-slate-500">“{s.nota}”</p>}
              {s.resolucion_nota && s.estatus !== "Pendiente" && (
                <p className="mt-0.5 text-xs text-slate-500">
                  {s.estatus} por {s.resuelto_por_email}: {s.resolucion_nota}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${BADGE[s.estatus] || "bg-slate-100 text-slate-600"}`}>
                {s.estatus}
              </span>
              {s.estatus === "Pendiente" && (
                <>
                  <Button data-testid={`producto-extra-aprobar-${s.id}`} size="sm"
                    disabled={busy === s.id} onClick={() => aprobar(s)}
                    className="bg-accent hover:bg-forest-700">
                    {busy === s.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
                    Aprobar y ampliar cupo
                  </Button>
                  <Button data-testid={`producto-extra-rechazar-${s.id}`} size="sm" variant="outline"
                    disabled={busy === s.id}
                    onClick={() => { setRechazo(s); setMotivo(""); }}>
                    <XCircle className="mr-1.5 h-4 w-4" /> Rechazar
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!rechazo} onOpenChange={(o) => !o && setRechazo(null)}>
        <DialogContent className="sm:max-w-md" data-testid="producto-extra-rechazo-dialog">
          <DialogHeader>
            <DialogTitle>Rechazar solicitud de {rechazo?.entidad_nombre}</DialogTitle>
          </DialogHeader>
          <Textarea data-testid="producto-extra-rechazo-motivo" rows={4} value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Motivo del rechazo (lo verá el administrador de la entidad)" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRechazo(null)}>Cancelar</Button>
            <Button data-testid="producto-extra-rechazo-confirmar" onClick={confirmarRechazo}
              disabled={busy === rechazo?.id} className="bg-red-600 hover:bg-red-700">
              Rechazar solicitud
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
